// Helpers to group time-stamped items (events, messages) by their day in the
// Spanish timezone and to render Spanish date headers ("Hoy", "Ayer", ...).

// Format a date header in Spanish, relative to today when possible.
export const formatDateHeader = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Convert to Spanish timezone for comparison
  const dateInSpain = new Date(date.toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
  const todayInSpain = new Date(today.toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
  const yesterdayInSpain = new Date(yesterday.toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));

  const isSameDay = (d1: Date, d2: Date) =>
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear();

  if (isSameDay(dateInSpain, todayInSpain)) {
    return 'Hoy';
  } else if (isSameDay(dateInSpain, yesterdayInSpain)) {
    return 'Ayer';
  } else {
    return dateInSpain.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: dateInSpain.getFullYear() !== todayInSpain.getFullYear() ? 'numeric' : undefined,
    });
  }
};

// Get a stable per-day key (in Spanish timezone) used to group items.
export const getDateKey = (dateString: string): string => {
  const date = new Date(dateString);
  const dateInSpain = new Date(date.toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
  return dateInSpain.toLocaleDateString('es-ES');
};

export interface DateGroup<T> {
  dateKey: string;
  dateHeader: string;
  items: T[];
}

// Group an array of items by day, preserving insertion order. `getDate`
// extracts the ISO date string from each item.
export const groupByDate = <T>(
  items: readonly T[],
  getDate: (item: T) => string
): DateGroup<T>[] => {
  const groups = new Map<string, T[]>();

  items.forEach((item) => {
    const dateKey = getDateKey(getDate(item));
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(item);
  });

  return Array.from(groups.entries()).map(([dateKey, groupItems]) => ({
    dateKey,
    dateHeader: formatDateHeader(getDate(groupItems[0])),
    items: groupItems,
  }));
};
